import { isbot } from 'isbot'

import { isForgedBrowserUa } from '@buffer-mono/segment-bot-signatures'

import { isPublicPublishPath } from './publicPublishPaths'

/**
 * Which signal matched, so a page view that skipped analytics stays
 * distinguishable from one where analytics failed to load.
 */
export type PublicPageBotSignal = 'selfIdentifiedBot' | 'forgedBrowserUa'

/** The parts of `window` the guard reads; also the seam tests write to. */
export type BotGuardView = {
  location: { pathname: string }
  navigator: { userAgent: string }
}

/**
 * Suppresses analytics for recognized bots on public Publish routes.
 * This browser check does not control access to pages or APIs.
 *
 * @see @buffer-mono/segment-bot-signatures for shared forged UA signatures
 */
export function getPublicPageBotSignal({
  pathname,
  userAgent,
}: {
  pathname: string
  userAgent: string | null | undefined
}): PublicPageBotSignal | null {
  if (!isPublicPublishPath(pathname)) {
    return null
  }

  // A missing or non-string header is a broken environment, not a bot: the
  // `isbot` pattern has no type guard of its own and would match the coerced
  // form of a non-string value.
  if (typeof userAgent !== 'string' || userAgent === '') {
    return null
  }

  if (isbot(userAgent)) {
    return 'selfIdentifiedBot'
  }

  if (isForgedBrowserUa(userAgent)) {
    return 'forgedBrowserUa'
  }

  return null
}

/**
 * {@link getPublicPageBotSignal} for the current page. Fails open: an
 * environment where the location or the user agent can't be read is a
 * detection failure, and detection failures keep today's human behavior.
 */
export function getCurrentPublicPageBotSignal(
  view: BotGuardView = window,
): PublicPageBotSignal | null {
  try {
    return getPublicPageBotSignal({
      pathname: view.location.pathname,
      userAgent: view.navigator.userAgent,
    })
  } catch {
    return null
  }
}

/**
 * Boolean form of {@link getCurrentPublicPageBotSignal}, for callers that only
 * decide whether to bootstrap a tracker.
 */
export function isPublicPageBot(view: BotGuardView = window): boolean {
  return getCurrentPublicPageBotSignal(view) !== null
}
